import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { matchPath, useParams } from "react-router-dom";

import { useIsSuperAdmin } from "@/api/is-superadmin";
import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import {
  PublicOrganisation,
  RoleEnum,
  RoleGroupEnum,
  UpdateUserPermissionsDto,
} from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateUserPermissions } from "../api/update-user-permissions";
import {
  UserPermissionsFormData,
  userPermissionsSchema,
} from "../types/user-permissions.schema";

const descriptionToRoleGroup: Record<RoleGroupEnum, string> = {
  [RoleGroupEnum.SESSION_GUEST]:
    "This role is ideal for users who are not part of the organisation but are invited to a session and need to join the session.",
  [RoleGroupEnum.VIEWER]:
    "This role is ideal for users who only need to view content without making any changes.",
  [RoleGroupEnum.USER]: `This role is designed for users who require standard access to features, allowing interaction with the application but without administrative privileges.`,
  [RoleGroupEnum.ADMIN]:
    "This role suits the majority of users, giving them access to most features, without edit permissions.",
};

const defaultInitialData: UserPermissionsFormData = {
  roleGroup: RoleGroupEnum.USER,
  isOrganisationManagementEnabled: false,
  isUserManagementEnabled: false,
  isJobPostingEnabled: false,
  isEventManagementEnabled: false,
};

type UserPermissionsFormApiData = {
  roles?: RoleEnum[];
  roleGroup?: RoleGroupEnum;
};

type UserPermissionsFormProps = {
  initialData: UserPermissionsFormApiData;
  userId: string;
  organisationData: PublicOrganisation;
};

function transformApiDataToFormData(
  initialData: UserPermissionsFormApiData,
): UserPermissionsFormData {
  const result = {
    ...defaultInitialData,
    roleGroup: RoleGroupEnum.USER,
  };

  if (!initialData.roleGroup) {
    return result;
  }

  result.roleGroup = initialData.roleGroup;

  const permissions = initialData.roles || [];

  if (permissions.includes(RoleEnum.ORGANISATION_MANAGEMENT)) {
    result.isOrganisationManagementEnabled = true;
  }

  if (permissions.includes(RoleEnum.USER_MANAGEMENT)) {
    result.isUserManagementEnabled = true;
  }

  if (permissions.includes(RoleEnum.JOB_CREATE)) {
    result.isJobPostingEnabled = true;
  }

  if (permissions.includes(RoleEnum.EVENTS_CREATE)) {
    result.isEventManagementEnabled = true;
  }

  return result;
}

export function UserPermissionsForm(props: UserPermissionsFormProps) {
  const { initialData, userId, organisationData } = props;
  const { organisation } = useOrganisation();
  const { id: paramOrgId } = useParams<{ id: string }>();
  const isSuperAdminView =
    matchPath(
      "/admin/organisations/:orgId/users/:userId",
      location.pathname,
    ) !== null;

  /**
   * This form is rendered two different ways:
   * 1. On the Super admin -> Organisations -> Users -> User
   * This case we want to use the orgId from the URL.
   * 2. On the Settings -> Users -> User
   * This case we want to use the orgId from the organisation context.
   */
  const orgId = isSuperAdminView ? (paramOrgId ?? "") : organisation.id;

  const initialFormData = transformApiDataToFormData(initialData);
  const form = useForm<UserPermissionsFormData>({
    defaultValues: initialFormData,
    resolver: zodResolver(userPermissionsSchema),
  });

  const { mutateAsync: updateUserPermissions } = useUpdateUserPermissions({
    onSuccess: (result) => {
      form.reset(transformApiDataToFormData(result[orgId]));
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  async function handleSubmit(data: UserPermissionsFormData) {
    const payload: UpdateUserPermissionsDto = {
      roleGroup: data.roleGroup,
      permissions: [],
    };

    if (data.isOrganisationManagementEnabled) {
      payload.permissions?.push(RoleEnum.ORGANISATION_MANAGEMENT);
    }

    if (data.isUserManagementEnabled) {
      payload.permissions?.push(RoleEnum.USER_MANAGEMENT);
    }

    if (data.isEventManagementEnabled) {
      payload.permissions?.push(RoleEnum.EVENTS_CREATE);
    }

    if (data.isJobPostingEnabled) {
      const jobManagementPermissions: RoleEnum[] = [
        RoleEnum.JOBS_VIEW,
        RoleEnum.JOB_CREATE,
      ];
      payload.permissions = [
        ...(payload?.permissions || []),
        ...jobManagementPermissions,
      ];
    }
    try {
      await updateUserPermissions({
        orgId,
        userId,
        payload,
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  const roleGroup = form.watch("roleGroup");
  const isInitialRender = useRef(true);

  // Handles updating the form when the role group is changed
  useEffect(() => {
    // If it's the initial render, we don't need to update the form
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    // If the role group is reverted let's get back the original values
    if (roleGroup === initialFormData.roleGroup) {
      form.setValue(
        "isOrganisationManagementEnabled",
        initialFormData.isOrganisationManagementEnabled,
      );
      form.setValue(
        "isUserManagementEnabled",
        initialFormData.isUserManagementEnabled,
      );
      form.setValue("isJobPostingEnabled", initialFormData.isJobPostingEnabled);
      form.setValue(
        "isEventManagementEnabled",
        initialFormData.isEventManagementEnabled,
      );
      return;
    }

    // If the role group has changed, we need to update the form and disable all the permissions
    form.setValue("isOrganisationManagementEnabled", false);
    form.setValue("isUserManagementEnabled", false);
    form.setValue("isJobPostingEnabled", false);
    form.setValue("isEventManagementEnabled", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleGroup]);

  const { isSuperAdmin } = useIsSuperAdmin();
  const isAdminToggleVisible =
    organisation.roleGroup === RoleGroupEnum.ADMIN || isSuperAdmin;

  const isJobPostingToggleVisible =
    organisationData.license?.isGivenMarketplaceAccess;
  const isEventManagementToggleVisible =
    organisationData.license?.isGivenEventAccess;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <Card.Header
            title="Permissions"
            description="Manage user permissions"
          />
          <Card.Content>
            <div>
              <FormLabel>Permissions</FormLabel>
              <FormDescription>
                What permission level should self service users initially join
                as? Permissions can be manually updated on a per-user basis at
                any time.
              </FormDescription>
            </div>
            <div className="mt-4">
              <div className="grid grid-cols-2 gap-x-6 md:grid">
                <div>
                  <FormLabel>User Role</FormLabel>
                  <FormDescription>
                    Give users a broad allocation of permissions
                  </FormDescription>
                </div>
                <FormField
                  control={form.control}
                  name="roleGroup"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-4">
                      <FormControl>
                        <ToggleGroup
                          type="single"
                          onValueChange={field.onChange}
                          value={field.value}
                          className="relative flex gap-x-0 divide-x divide-border rounded-md border border-border shadow-sm"
                        >
                          <ToggleGroupItem
                            value={RoleGroupEnum.SESSION_GUEST}
                            variant="ghost"
                          >
                            Guest
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value={RoleGroupEnum.VIEWER}
                            variant="ghost"
                          >
                            Student
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value={RoleGroupEnum.USER}
                            variant="ghost"
                          >
                            User
                          </ToggleGroupItem>
                          {isAdminToggleVisible && (
                            <ToggleGroupItem
                              value={RoleGroupEnum.ADMIN}
                              variant="ghost"
                            >
                              Admin
                            </ToggleGroupItem>
                          )}
                        </ToggleGroup>
                      </FormControl>
                      <Card className="mt-4 rounded-md px-3 py-2">
                        <p className="mb-0 text-xs text-muted">
                          {descriptionToRoleGroup[field.value]}
                        </p>
                      </Card>
                    </FormItem>
                  )}
                />
              </div>
              <motion.div
                className="overflow-hidden"
                initial={{ opacity: 0, height: 0 }}
                animate={
                  roleGroup !== RoleGroupEnum.VIEWER &&
                  roleGroup !== RoleGroupEnum.SESSION_GUEST
                    ? { opacity: 1, height: "auto" }
                    : { opacity: 0, height: 0 }
                }
                transition={{ duration: 0.3 }}
              >
                <div>
                  <Separator className="my-6" />
                  <div className="grid grid-cols-2 gap-x-6 md:grid">
                    <div>
                      <FormLabel>Organisation Management</FormLabel>
                      <FormDescription>
                        Users are able to access organisational settings and
                        management.
                      </FormDescription>
                    </div>
                    <div className="flex flex-col gap-6">
                      {roleGroup === RoleGroupEnum.ADMIN && (
                        <FormField
                          control={form.control}
                          name="isOrganisationManagementEnabled"
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-2 lg:flex-row lg:items-center">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <div>
                                <FormLabel>Organisation Management</FormLabel>
                                <FormDescription>
                                  Users can manage organisation details
                                </FormDescription>
                              </div>
                            </FormItem>
                          )}
                        />
                      )}
                      {roleGroup === RoleGroupEnum.USER && (
                        <FormField
                          control={form.control}
                          name="isUserManagementEnabled"
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-2 lg:flex-row lg:items-center">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <div>
                                <FormLabel>User Management</FormLabel>
                                <FormDescription>
                                  The user is allowed to manage existing users
                                  or invite new users to the organisation
                                </FormDescription>
                              </div>
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </div>
                  {roleGroup !== RoleGroupEnum.VIEWER && (
                    <div>
                      {isJobPostingToggleVisible && (
                        <>
                          <Separator className="my-6" />
                          <div className="grid grid-cols-2 gap-x-6 md:grid">
                            <div>
                              <FormLabel>Job Posting</FormLabel>
                              <FormDescription>
                                Users are permitted to advertise positions that
                                they are either looking to fill (hire for), or
                                additional resource they are looking to offload.
                              </FormDescription>
                            </div>
                            <div className="flex flex-col gap-6">
                              <FormField
                                control={form.control}
                                name="isJobPostingEnabled"
                                render={({ field }) => (
                                  <FormItem className="flex flex-col gap-2 lg:flex-row lg:items-center">
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                    <div>
                                      <FormLabel>Access Enabled</FormLabel>
                                      <FormDescription>
                                        Users can access this feature
                                      </FormDescription>
                                    </div>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {isEventManagementToggleVisible && (
                        <>
                          <Separator className="my-6" />
                          <div className="grid grid-cols-2 gap-x-6 md:grid">
                            <div>
                              <FormLabel>Event Management</FormLabel>
                              <FormDescription>
                                Users are allowed to create and manage events.
                              </FormDescription>
                            </div>
                            <div className="flex flex-col gap-6">
                              <FormField
                                control={form.control}
                                name="isEventManagementEnabled"
                                render={({ field }) => (
                                  <FormItem className="flex flex-col gap-2 lg:flex-row lg:items-center">
                                    <FormControl>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                    <div>
                                      <FormLabel>Access Enabled</FormLabel>
                                      <FormDescription>
                                        Users can access this feature
                                      </FormDescription>
                                    </div>
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
            {form.formState.errors.root && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error updating permissions</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" } // 224px is equivalent to h-56
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  form.reset(transformApiDataToFormData(initialData))
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </Card>
      </form>
    </Form>
  );
}
